import type { Placement } from "./types";

export interface Rect { top: number; left: number; width: number; height: number }
export interface Size { width: number; height: number }

export const CUTOUT_PAD = 6;
export const VIEWPORT_MARGIN = 12;
export const CARD_GAP = 12;

type Side = Exclude<Placement, "auto">;
const ORDER: Side[] = ["bottom", "top", "right", "left"];

export function cutoutRect(target: Rect): Rect {
  return {
    top: target.top - CUTOUT_PAD,
    left: target.left - CUTOUT_PAD,
    width: target.width + 2 * CUTOUT_PAD,
    height: target.height + 2 * CUTOUT_PAD,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function candidate(side: Side, t: Rect, card: Size): { top: number; left: number } {
  const cx = t.left + t.width / 2;
  const cy = t.top + t.height / 2;
  const off = CUTOUT_PAD + CARD_GAP;
  switch (side) {
    case "bottom": return { top: t.top + t.height + off, left: cx - card.width / 2 };
    case "top": return { top: t.top - off - card.height, left: cx - card.width / 2 };
    case "right": return { top: cy - card.height / 2, left: t.left + t.width + off };
    case "left": return { top: cy - card.height / 2, left: t.left - off - card.width };
  }
}

function fits(side: Side, pos: { top: number; left: number }, card: Size, vp: Size): boolean {
  const m = VIEWPORT_MARGIN;
  // The primary axis must fit without clamping; the cross axis is clamped later.
  if (side === "bottom" || side === "top") {
    return pos.top >= m && pos.top + card.height <= vp.height - m;
  }
  return pos.left >= m && pos.left + card.width <= vp.width - m;
}

function clampPos(
  pos: { top: number; left: number }, card: Size, vp: Size,
): { top: number; left: number } {
  const m = VIEWPORT_MARGIN;
  return {
    top: clamp(pos.top, m, Math.max(m, vp.height - m - card.height)),
    left: clamp(pos.left, m, Math.max(m, vp.width - m - card.width)),
  };
}

export function placeCard(
  target: Rect, card: Size, viewport: Size, wanted: Placement,
): { top: number; left: number; placement: Side } {
  const order: Side[] = wanted === "auto" ? ORDER : [wanted, ...ORDER.filter((s) => s !== wanted)];
  for (const side of order) {
    const pos = candidate(side, target, card);
    if (fits(side, pos, card, viewport)) {
      return { ...clampPos(pos, card, viewport), placement: side };
    }
  }
  const pos = candidate("bottom", target, card);
  return { ...clampPos(pos, card, viewport), placement: "bottom" };
}

export function centerCard(card: Size, viewport: Size): { top: number; left: number } {
  return clampPos(
    { top: (viewport.height - card.height) / 2, left: (viewport.width - card.width) / 2 },
    card, viewport,
  );
}
